import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { formatMedida, formatDate, formatDateTime } from "@/lib/format";
import { Search, Printer, FileText, Loader2, Eye, X, Plus, Share2, Pencil, Trash2, Ban, AlertTriangle, Check, History } from "lucide-react";
import { toast } from "sonner";
import { sharePdf, logAcesso } from "@/lib/share";

const STATUS = ["Pendente", "Assinado e retornado", "Perdido", "Cancelado"];
const STATUS_CLS = {
  Pendente: "bg-amber-100 text-amber-800 border-amber-300",
  "Assinado e retornado": "bg-green-100 text-green-800 border-green-300",
  Perdido: "bg-red-100 text-red-800 border-red-300",
  Cancelado: "bg-slate-200 text-slate-600 border-slate-400",
};

export default function Romaneios() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { hasPerm } = useAuth();
  const canCriar = hasPerm("romaneio:criar");
  const canEditar = hasPerm("romaneio:editar");
  const canStatus = hasPerm("romaneio:alterar_status");
  const canCancelar = hasPerm("romaneio:cancelar");
  const canPdf = hasPerm("romaneio:pdf");
  const [romaneios, setRomaneios] = useState([]);
  const [q, setQ] = useState("");
  const [fCliente, setFCliente] = useState("");
  const [fDestino, setFDestino] = useState("");
  const [fStatus, setFStatus] = useState("");
  const [fDe, setFDe] = useState("");
  const [fAte, setFAte] = useState("");
  const [ordem, setOrdem] = useState("data_desc");
  const [loading, setLoading] = useState(true);
  const [detalhe, setDetalhe] = useState(null);
  const [editando, setEditando] = useState(false);
  const [novoCodigo, setNovoCodigo] = useState("");
  const [novoBloco, setNovoBloco] = useState("principal");
  const [busy, setBusy] = useState(false);
  const [cancelConfirm, setCancelConfirm] = useState(null);
  const [avail, setAvail] = useState([]);
  const [selCombo, setSelCombo] = useState("");
  const [selRolo, setSelRolo] = useState("");
  const [metaForm, setMetaForm] = useState({ ref: "", cliente: "", destino: "", observacao: "" });
  const [savingMeta, setSavingMeta] = useState(false);

  const loadAvail = async () => {
    try {
      const r = await api.get("/rolos", { params: { status: "Disponível" } });
      setAvail(r.data);
    } catch (e) {
      /* silencioso */
    }
  };

  const load = () => {
    setLoading(true);
    api
      .get("/romaneios")
      .then((r) => setRomaneios(r.data))
      .catch((e) => {const _m=apiError(e); if(_m) toast.error(_m);})
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
  }, []);

  // Deep link: /romaneios?codigo=ROM-XXXX abre o detalhe direto (usado pelo rastreio de rolo)
  useEffect(() => {
    const codigo = searchParams.get("codigo");
    if (!codigo || !romaneios.length) return;
    const alvo = romaneios.find((r) => String(r.codigo).toUpperCase() === String(codigo).toUpperCase());
    if (alvo) {
      verDetalhe(alvo);
      // Limpa o param após abrir para não reabrir em cada re-render
      searchParams.delete("codigo");
      setSearchParams(searchParams, { replace: true });
    }
  }, [romaneios]);

  const clientes = useMemo(
    () => [...new Set(romaneios.map((r) => r.cliente).filter(Boolean))].sort((a, b) => a.localeCompare(b, "pt-BR")),
    [romaneios]
  );
  const destinos = useMemo(
    () => [...new Set(romaneios.map((r) => r.destino).filter(Boolean))].sort((a, b) => a.localeCompare(b, "pt-BR")),
    [romaneios]
  );

  const filtrados = useMemo(() => {
    const t = q.trim().toLowerCase();
    const ymd = (iso) => String(iso || "").slice(0, 10);
    let arr = romaneios.filter((r) => {
      if (t && ![r.codigo, r.ref, r.cliente, r.destino].filter(Boolean).some((v) => String(v).toLowerCase().includes(t))) return false;
      if (fCliente && r.cliente !== fCliente) return false;
      if (fDestino && r.destino !== fDestino) return false;
      if (fStatus && r.status !== fStatus) return false;
      const d = ymd(r.data);
      if (fDe && d < fDe) return false;
      if (fAte && d > fAte) return false;
      return true;
    });
    const refNum = (r) => {
      const n = parseInt(String(r.ref).replace(/\D/g, ""), 10);
      return isNaN(n) ? 0 : n;
    };
    const dataMs = (r) => new Date(r.data || 0).getTime();
    arr = [...arr].sort((a, b) => {
      switch (ordem) {
        case "data_asc": return dataMs(a) - dataMs(b);
        case "ref_desc": return refNum(b) - refNum(a);
        case "ref_asc": return refNum(a) - refNum(b);
        case "cliente_az": return String(a.cliente || "").localeCompare(String(b.cliente || ""), "pt-BR") || dataMs(b) - dataMs(a);
        case "destino_az": return String(a.destino || "").localeCompare(String(b.destino || ""), "pt-BR") || dataMs(b) - dataMs(a);
        case "rolos_desc": return (b.total_rolos || 0) - (a.total_rolos || 0);
        case "rolos_asc": return (a.total_rolos || 0) - (b.total_rolos || 0);
        default: return dataMs(b) - dataMs(a);
      }
    });
    return arr;
  }, [romaneios, q, fCliente, fDestino, fStatus, fDe, fAte, ordem]);

  const totalRolosFiltro = useMemo(
    () => filtrados.reduce((s, r) => s + (r.total_rolos || 0), 0),
    [filtrados]
  );

  const itensOrdenados = useMemo(() => {
    if (!detalhe) return [];
    const rank = (i) => (i.bloco === "ribana" ? 1 : 0);
    return [...detalhe.itens].sort((a, b) =>
      rank(a) - rank(b) ||
      String(a.cor || "").localeCompare(String(b.cor || ""), "pt-BR") ||
      String(a.codigo || "").localeCompare(String(b.codigo || ""), "pt-BR")
    );
  }, [detalhe]);

  const totaisDetalhe = useMemo(() => {
    if (!detalhe) return { kg: 0, m: 0 };
    return (detalhe.itens || []).reduce(
      (acc, i) => {
        const v = Number(i.peso) || 0;
        if ((i.unidade || "kg") === "m") acc.m += v;
        else acc.kg += v;
        return acc;
      },
      { kg: 0, m: 0 }
    );
  }, [detalhe]);

  const selectCls = "h-10 rounded-md border-2 border-slate-300 px-2 text-sm outline-none focus:border-blue-500";

  const abrirPdf = (r) => {
    window.open(`${process.env.REACT_APP_BACKEND_URL}/api/romaneios/${r.id}/pdf`, "_blank");
    logAcesso({ tipo: "romaneio_impresso", documento_tipo: "romaneio", documento_id: r.id, documento_codigo: r.codigo, ref: r.ref });
  };

  const compartilhar = async (r) => {
    await sharePdf(`/romaneios/${r.id}/pdf`, `Romaneio - REF ${r.ref}.pdf`, `Romaneio REF ${r.ref}`);
    logAcesso({ tipo: "romaneio_compartilhado", documento_tipo: "romaneio", documento_id: r.id, documento_codigo: r.codigo, ref: r.ref });
  };

  const mudarStatus = async (r, status) => {
    try {
      const res = await api.patch(`/romaneios/${r.id}/status`, { status });
      setRomaneios((prev) => prev.map((x) => (x.id === r.id ? res.data : x)));
      toast.success(`REF ${r.ref}: ${status}`);
    } catch (e) {
      {const _m=apiError(e); if(_m) toast.error(_m);};
    }
  };

  const verDetalhe = async (r, edit0 = false) => {
    try {
      const res = await api.get(`/romaneios/${r.id}`);
      setDetalhe(res.data);
      setMetaForm({
        ref: res.data.ref || "",
        cliente: res.data.cliente || "",
        destino: res.data.destino || "",
        observacao: res.data.observacao || "",
      });
      const edit = edit0 && res.data.status === "Pendente";
      setEditando(edit);
      if (edit) loadAvail();
    } catch (e) {
      {const _m=apiError(e); if(_m) toast.error(_m);};
    }
  };

  const refreshDetalhe = (data) => {
    setDetalhe(data);
    setMetaForm({
      ref: data.ref || "",
      cliente: data.cliente || "",
      destino: data.destino || "",
      observacao: data.observacao || "",
    });
    setRomaneios((prev) => prev.map((x) => (x.id === data.id ? data : x)));
  };

  const metaChanged =
    !!detalhe &&
    (metaForm.ref.trim() !== (detalhe.ref || "").trim() ||
      metaForm.cliente.trim() !== (detalhe.cliente || "").trim() ||
      metaForm.destino.trim() !== (detalhe.destino || "").trim() ||
      metaForm.observacao.trim() !== (detalhe.observacao || "").trim());

  const salvarMeta = async () => {
    if (!detalhe || !metaChanged) return;
    if (!metaForm.ref.trim()) return toast.error("REF não pode ficar vazio");
    if (!metaForm.destino.trim()) return toast.error("Destino não pode ficar vazio");
    setSavingMeta(true);
    try {
      const res = await api.patch(`/romaneios/${detalhe.id}`, {
        ref: metaForm.ref.trim(),
        cliente: metaForm.cliente.trim() || null,
        destino: metaForm.destino.trim(),
        observacao: metaForm.observacao.trim() || null,
      });
      refreshDetalhe(res.data);
      toast.success("Romaneio atualizado");
    } catch (e) {
      {const _m=apiError(e); if(_m) toast.error(_m);};
    } finally {
      setSavingMeta(false);
    }
  };

  const descartarMeta = () => {
    if (!detalhe) return;
    setMetaForm({
      ref: detalhe.ref || "",
      cliente: detalhe.cliente || "",
      destino: detalhe.destino || "",
      observacao: detalhe.observacao || "",
    });
  };

  const adicionarRolo = async () => {
    const code = novoCodigo.trim().toUpperCase();
    if (!code) return;
    setBusy(true);
    try {
      const found = await api.get(`/rolos/codigo/${code}`);
      const res = await api.post(`/romaneios/${detalhe.id}/itens`, { rolo_id: found.data.id, bloco: novoBloco });
      refreshDetalhe(res.data);
      setNovoCodigo("");
      loadAvail();
      toast.success(`${code} adicionado`);
    } catch (e) {
      {const _m=apiError(e); if(_m) toast.error(_m);};
    } finally {
      setBusy(false);
    }
  };

  const adicionarSelecionado = async () => {
    if (!selRolo) return;
    setBusy(true);
    try {
      const res = await api.post(`/romaneios/${detalhe.id}/itens`, { rolo_id: selRolo, bloco: novoBloco });
      refreshDetalhe(res.data);
      setSelRolo("");
      loadAvail();
      toast.success("Rolo adicionado");
    } catch (e) {
      {const _m=apiError(e); if(_m) toast.error(_m);};
    } finally {
      setBusy(false);
    }
  };

  const removerRolo = async (rolo_id, codigo) => {
    setBusy(true);
    try {
      const res = await api.delete(`/romaneios/${detalhe.id}/itens/${rolo_id}`);
      refreshDetalhe(res.data);
      loadAvail();
      toast.success(`${codigo} removido e devolvido ao estoque`);
    } catch (e) {
      {const _m=apiError(e); if(_m) toast.error(_m);};
    } finally {
      setBusy(false);
    }
  };

  const cancelarRomaneio = async () => {
    setBusy(true);
    try {
      const res = await api.post(`/romaneios/${cancelConfirm.id}/cancelar`);
      refreshDetalhe(res.data.romaneio);
      setCancelConfirm(null);
      setEditando(false);
      toast.success(`Romaneio cancelado — ${res.data.devolvidos} rolo(s) devolvidos ao estoque`);
    } catch (e) {
      {const _m=apiError(e); if(_m) toast.error(_m);};
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="romaneios-page">
      <div className="mb-6 flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-bold tracking-tight text-slate-900 md:text-3xl">
            <FileText size={28} /> Romaneios
          </h1>
          <p className="mt-1 text-sm text-slate-500">
            Todos os romaneios gerados. Crie um novo, busque, atualize o status e reimprima o PDF quando quiser.
          </p>
        </div>
        {canCriar && (
        <button
          onClick={() => navigate("/romaneio/novo")}
          data-testid="criar-romaneio-btn"
          className="flex items-center gap-2 rounded-md bg-slate-900 px-5 py-3 text-sm font-semibold text-white hover:bg-slate-800"
        >
          <Plus size={18} /> Criar romaneio
        </button>
        )}
      </div>

      <div className="mb-4 space-y-3">
        <div className="relative max-w-md">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            className="h-12 w-full rounded-md border-2 border-slate-300 pl-9 pr-3 text-base outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200"
            placeholder="Buscar por REF, cliente, destino..."
            value={q}
            onChange={(e) => setQ(e.target.value)}
            data-testid="romaneios-busca"
          />
        </div>
        <div className="flex flex-wrap items-center gap-2" data-testid="romaneios-filtros">
          <select value={fCliente} onChange={(e) => setFCliente(e.target.value)} className={selectCls} data-testid="rom-filtro-cliente">
            <option value="">Todos os clientes</option>
            {clientes.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
          <select value={fDestino} onChange={(e) => setFDestino(e.target.value)} className={selectCls} data-testid="rom-filtro-destino">
            <option value="">Todos os destinos</option>
            {destinos.map((d) => <option key={d} value={d}>{d}</option>)}
          </select>
          <select value={fStatus} onChange={(e) => setFStatus(e.target.value)} className={selectCls} data-testid="rom-filtro-status">
            <option value="">Todos os status</option>
            {STATUS.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <div className="flex items-center gap-1.5">
            <span className="text-xs font-medium text-slate-500">De</span>
            <input type="date" value={fDe} onChange={(e) => setFDe(e.target.value)} className={selectCls} data-testid="rom-filtro-de" />
            <span className="text-xs font-medium text-slate-500">Até</span>
            <input type="date" value={fAte} onChange={(e) => setFAte(e.target.value)} className={selectCls} data-testid="rom-filtro-ate" />
          </div>
          <div className="ml-auto flex items-center gap-2">
            <span className="text-xs font-medium text-slate-500">Ordenar:</span>
            <select value={ordem} onChange={(e) => setOrdem(e.target.value)} className={selectCls} data-testid="rom-ordenar">
              <option value="data_desc">Data (mais recente)</option>
              <option value="data_asc">Data (mais antigo)</option>
              <option value="ref_desc">Nº REF (maior → menor)</option>
              <option value="ref_asc">Nº REF (menor → maior)</option>
              <option value="cliente_az">Cliente (A→Z)</option>
              <option value="destino_az">Destino (A→Z)</option>
              <option value="rolos_desc">Rolos (maior → menor)</option>
              <option value="rolos_asc">Rolos (menor → maior)</option>
            </select>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-x-6 gap-y-1 rounded-md bg-slate-900 px-4 py-3 text-white" data-testid="romaneios-contagem">
          <div>
            <span className="text-2xl font-bold font-mono">{filtrados.length}</span>
            <span className="ml-2 text-sm font-medium text-slate-300">romaneio{filtrados.length === 1 ? "" : "s"}</span>
          </div>
          <div>
            <span className="text-2xl font-bold font-mono">{totalRolosFiltro}</span>
            <span className="ml-2 text-sm font-medium text-slate-300">rolos no total</span>
          </div>
          {(fDe || fAte) && (
            <div className="ml-auto text-xs text-slate-400">
              Período: {fDe ? fDe.split("-").reverse().join("/") : "início"} → {fAte ? fAte.split("-").reverse().join("/") : "hoje"}
            </div>
          )}
        </div>
      </div>

      <div className="overflow-x-auto rounded-md border-2 border-slate-200 bg-white">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b-2 border-slate-200 bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
              <th className="px-4 py-3">REF</th>
              <th className="px-4 py-3">Cliente</th>
              <th className="px-4 py-3">Destino</th>
              <th className="px-4 py-3">Data</th>
              <th className="px-4 py-3">Rolos</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3 text-right">Ações</th>
            </tr>
          </thead>
          <tbody data-testid="romaneios-table">
            {loading && (
              <tr>
                <td colSpan={7} className="px-4 py-10 text-center text-slate-400">
                  <Loader2 className="mx-auto animate-spin" />
                </td>
              </tr>
            )}
            {!loading && filtrados.length === 0 && (
              <tr>
                <td colSpan={7} className="px-4 py-10 text-center text-slate-400">
                  Nenhum romaneio encontrado.
                </td>
              </tr>
            )}
            {!loading &&
              filtrados.map((r) => (
                <tr key={r.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`romaneio-row-${r.codigo}`}>
                  <td className="px-4 py-3 font-bold text-slate-900">{r.ref}</td>
                  <td className="px-4 py-3">{r.cliente || "—"}</td>
                  <td className="px-4 py-3">{r.destino}</td>
                  <td className="px-4 py-3 whitespace-nowrap">{formatDate(r.data)}</td>
                  <td className="px-4 py-3 font-semibold">{r.total_rolos}</td>
                  <td className="px-4 py-3">
                    {r.status === "Cancelado" || !canStatus ? (
                      <span className={`rounded-md border-2 px-2 py-1 text-xs font-semibold ${STATUS_CLS[r.status]}`} data-testid={`romaneio-status-${r.codigo}`}>
                        {r.status}
                      </span>
                    ) : (
                      <select
                        value={r.status}
                        onChange={(e) => mudarStatus(r, e.target.value)}
                        data-testid={`romaneio-status-${r.codigo}`}
                        className={`rounded-md border-2 px-2 py-1 text-xs font-semibold outline-none ${STATUS_CLS[r.status] || ""}`}
                      >
                        {STATUS.filter((s) => s !== "Cancelado").map((s) => (
                          <option key={s} value={s}>
                            {s}
                          </option>
                        ))}
                      </select>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-2">
                      {r.status === "Pendente" && canEditar && (
                        <button
                          onClick={() => verDetalhe(r, true)}
                          data-testid={`romaneio-editar-${r.codigo}`}
                          className="flex items-center gap-1 rounded-md border-2 border-blue-200 bg-blue-50 px-2.5 py-1.5 text-xs font-semibold text-blue-700 hover:bg-blue-100"
                        >
                          <Pencil size={14} /> Editar
                        </button>
                      )}
                      <button
                        onClick={() => verDetalhe(r)}
                        data-testid={`romaneio-ver-${r.codigo}`}
                        className="flex items-center gap-1 rounded-md border-2 border-slate-200 px-2.5 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-100"
                      >
                        <Eye size={14} /> Ver
                      </button>
                      {canPdf && (
                        <button
                          onClick={() => abrirPdf(r)}
                          data-testid={`romaneio-pdf-${r.codigo}`}
                          className="flex items-center gap-1 rounded-md bg-slate-900 px-2.5 py-1.5 text-xs font-semibold text-white hover:bg-slate-800"
                        >
                          <Printer size={14} /> PDF
                        </button>
                      )}
                      {canPdf && (
                        <button
                          onClick={() => compartilhar(r)}
                          data-testid={`romaneio-share-${r.codigo}`}
                          className="flex items-center gap-1 rounded-md bg-blue-600 px-2.5 py-1.5 text-xs font-semibold text-white hover:bg-blue-700"
                        >
                          <Share2 size={14} /> Compartilhar
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>

      {detalhe && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => setDetalhe(null)}>
          <div
            className="max-h-[85vh] w-full max-w-lg overflow-y-auto rounded-md bg-white p-5"
            onClick={(e) => e.stopPropagation()}
            data-testid="romaneio-detalhe"
          >
            <div className="mb-3 flex items-start justify-between gap-2">
              <div className="min-w-0 flex-1">
                <h2 className="text-xl font-bold text-slate-900">
                  REF {detalhe.ref}
                  <span className="ml-2 text-base font-normal text-slate-500">
                    · {detalhe.cliente || "—"}
                  </span>
                </h2>
                <div className="mt-1 flex items-center gap-2">
                  <span className={`rounded-sm border px-2 py-0.5 text-xs font-semibold ${STATUS_CLS[detalhe.status] || ""}`}>
                    {detalhe.status}
                  </span>
                  <span className="text-xs text-slate-400">
                    {detalhe.total_rolos} rolo(s) · {formatDate(detalhe.data)}
                  </span>
                </div>
              </div>
              <button onClick={() => setDetalhe(null)} className="rounded-md p-1 text-slate-400 hover:bg-slate-100">
                <X size={20} />
              </button>
            </div>

            {detalhe.status === "Pendente" && canEditar ? (
              <div
                className="mb-3 rounded-md border-2 border-slate-200 bg-slate-50 p-3"
                data-testid="romaneio-meta-form"
              >
                <div className="mb-2 text-xs font-bold uppercase tracking-wide text-slate-500">
                  Dados do romaneio
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <label className="block text-xs font-semibold text-slate-600">
                    REF *
                    <input
                      value={metaForm.ref}
                      onChange={(e) => setMetaForm((f) => ({ ...f, ref: e.target.value }))}
                      data-testid="romaneio-meta-ref"
                      className="mt-1 h-10 w-full rounded-md border-2 border-slate-300 bg-white px-3 text-sm font-normal outline-none focus:border-blue-500"
                    />
                  </label>
                  <label className="block text-xs font-semibold text-slate-600">
                    Destino *
                    <input
                      value={metaForm.destino}
                      onChange={(e) => setMetaForm((f) => ({ ...f, destino: e.target.value }))}
                      data-testid="romaneio-meta-destino"
                      className="mt-1 h-10 w-full rounded-md border-2 border-slate-300 bg-white px-3 text-sm font-normal outline-none focus:border-blue-500"
                    />
                  </label>
                  <label className="col-span-2 block text-xs font-semibold text-slate-600">
                    Cliente
                    <input
                      value={metaForm.cliente}
                      onChange={(e) => setMetaForm((f) => ({ ...f, cliente: e.target.value }))}
                      data-testid="romaneio-meta-cliente"
                      className="mt-1 h-10 w-full rounded-md border-2 border-slate-300 bg-white px-3 text-sm font-normal outline-none focus:border-blue-500"
                    />
                  </label>
                  <label className="col-span-2 block text-xs font-semibold text-slate-600">
                    Observação
                    <input
                      value={metaForm.observacao}
                      onChange={(e) => setMetaForm((f) => ({ ...f, observacao: e.target.value }))}
                      data-testid="romaneio-meta-observacao"
                      className="mt-1 h-10 w-full rounded-md border-2 border-slate-300 bg-white px-3 text-sm font-normal outline-none focus:border-blue-500"
                    />
                  </label>
                </div>
                {metaChanged && (
                  <div className="mt-2 flex flex-wrap items-center justify-end gap-2">
                    <button
                      type="button"
                      onClick={descartarMeta}
                      disabled={savingMeta}
                      data-testid="romaneio-meta-descartar"
                      className="rounded-md border-2 border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-100 disabled:opacity-50"
                    >
                      Descartar
                    </button>
                    <button
                      type="button"
                      onClick={salvarMeta}
                      disabled={savingMeta}
                      data-testid="romaneio-meta-salvar"
                      className="flex items-center gap-1 rounded-md bg-blue-600 px-3 py-1.5 text-xs font-bold text-white hover:bg-blue-700 disabled:opacity-50"
                    >
                      {savingMeta ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
                      Salvar alterações
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <div className="mb-3 rounded-md bg-slate-50 p-3 text-sm text-slate-600">
                <div>
                  <strong className="text-slate-800">Destino:</strong> {detalhe.destino}
                </div>
                {detalhe.cliente && (
                  <div>
                    <strong className="text-slate-800">Cliente:</strong> {detalhe.cliente}
                  </div>
                )}
                {detalhe.observacao && (
                  <div>
                    <strong className="text-slate-800">Obs.:</strong> {detalhe.observacao}
                  </div>
                )}
                <div className="mt-1 text-xs italic text-slate-400">
                  Edição bloqueada — status {detalhe.status}
                </div>
              </div>
            )}
            <div className="mt-2 text-sm text-slate-500">{detalhe.descricao_peca || "—"}</div>
            <ul className="mt-3 divide-y divide-slate-100 rounded-md border border-slate-100">
              {itensOrdenados.map((i) => (
                <li key={i.rolo_id} className="flex items-center justify-between gap-2 px-3 py-2 text-sm">
                  <span className="min-w-0">
                    <span className="font-mono text-xs text-slate-400">{i.codigo} </span>
                    {i.tipo ? `${i.tipo} ${i.cor}` : i.cor} <span className="text-slate-400">({i.fornecedor_nome})</span>
                    {i.bloco === "ribana" && <span className="ml-1 text-xs text-amber-600">· ribana</span>}
                  </span>
                  <span className="flex shrink-0 items-center gap-2">
                    <span className="font-mono font-semibold">{formatMedida(i.peso, i.unidade)}</span>
                    {editando && (
                      <button
                        onClick={() => removerRolo(i.rolo_id, i.codigo)}
                        disabled={busy}
                        data-testid={`rom-remover-${i.rolo_id}`}
                        title="Remover e devolver ao estoque"
                        className="rounded-md border border-red-200 bg-red-50 p-1 text-red-600 hover:bg-red-100 disabled:opacity-40"
                      >
                        <Trash2 size={15} />
                      </button>
                    )}
                  </span>
                </li>
              ))}
            </ul>

            {(totaisDetalhe.kg > 0 || totaisDetalhe.m > 0) && (
              <div
                data-testid="romaneio-detalhe-total"
                className="mt-3 flex flex-wrap items-center justify-end gap-x-5 gap-y-1 rounded-md border-2 border-slate-900 bg-slate-900 px-4 py-3 text-white"
              >
                {totaisDetalhe.kg > 0 && (
                  <div className="flex items-baseline gap-2" data-testid="romaneio-detalhe-total-kg">
                    <span className="text-xs font-medium uppercase tracking-wide text-slate-300">Total:</span>
                    <span className="font-mono text-lg font-bold">
                      {totaisDetalhe.kg.toFixed(2).replace(".", ",")} kg
                    </span>
                  </div>
                )}
                {totaisDetalhe.m > 0 && (
                  <div className="flex items-baseline gap-2" data-testid="romaneio-detalhe-total-m">
                    <span className="text-xs font-medium uppercase tracking-wide text-slate-300">Total:</span>
                    <span className="font-mono text-lg font-bold">
                      {totaisDetalhe.m.toFixed(2).replace(".", ",")} m
                    </span>
                  </div>
                )}
              </div>
            )}

            {(detalhe.historico_edicoes || []).length > 0 && (
              <details className="mt-3 rounded-md border-l-4 border-blue-300 bg-blue-50/50 p-2 text-xs" data-testid="romaneio-historico-edicoes">
                <summary className="flex cursor-pointer items-center gap-1 font-semibold text-slate-700">
                  <History size={12} /> Histórico de edições ({detalhe.historico_edicoes.length})
                </summary>
                <ul className="mt-2 space-y-2 pl-1">
                  {detalhe.historico_edicoes.slice().reverse().map((ev, idx) => (
                    <li key={idx} className="border-l-2 border-blue-200 pl-2">
                      <div className="text-slate-500">
                        {formatDateTime(ev.data_hora)} · <strong className="text-slate-800">{ev.usuario_nome}</strong>
                      </div>
                      <ul className="mt-0.5 space-y-0.5">
                        {(ev.mudancas || []).map((m, i) => (
                          <li key={i} className="text-slate-600">
                            <span className="font-semibold capitalize">{m.campo === "observacao" ? "obs." : m.campo}:</span>{" "}
                            <span className="text-slate-400 line-through">{m.antes || "—"}</span>
                            {" → "}
                            <span className="font-semibold text-slate-800">{m.depois || "—"}</span>
                          </li>
                        ))}
                      </ul>
                    </li>
                  ))}
                </ul>
              </details>
            )}

            {editando && (
              <div className="mt-3 rounded-md border-2 border-dashed border-slate-300 p-3" data-testid="rom-add-box">
                <div className="mb-2 flex items-center gap-2">
                  <span className="text-xs font-semibold text-slate-500">Bloco:</span>
                  <select value={novoBloco} onChange={(e) => setNovoBloco(e.target.value)} data-testid="rom-add-bloco" className="h-9 rounded-md border-2 border-slate-300 px-2 text-sm">
                    <option value="principal">Tecido Principal</option>
                    <option value="ribana">Ribana</option>
                  </select>
                </div>

                <div className="mb-1 text-xs font-semibold text-slate-500">Escolher da lista (rolos disponíveis)</div>
                <div className="flex flex-wrap items-center gap-2">
                  <select
                    value={selCombo}
                    onChange={(e) => { setSelCombo(e.target.value); setSelRolo(""); }}
                    data-testid="rom-add-combo"
                    className="h-10 flex-1 rounded-md border-2 border-slate-300 px-2 text-sm outline-none focus:border-blue-500"
                  >
                    <option value="">Escolha o tecido…</option>
                    {[...new Map(avail.map((r) => [r.combinacao_id, r])).values()]
                      .sort((a, b) => `${a.tipo} ${a.cor}`.localeCompare(`${b.tipo} ${b.cor}`, "pt-BR"))
                      .map((r) => (
                        <option key={r.combinacao_id} value={r.combinacao_id}>
                          {r.tipo} · {r.cor} ({r.fornecedor_nome}) — {avail.filter((x) => x.combinacao_id === r.combinacao_id).length} disp.
                        </option>
                      ))}
                  </select>
                  <select
                    value={selRolo}
                    onChange={(e) => setSelRolo(e.target.value)}
                    disabled={!selCombo}
                    data-testid="rom-add-rolo"
                    className="h-10 flex-1 rounded-md border-2 border-slate-300 px-2 text-sm outline-none focus:border-blue-500 disabled:bg-slate-50"
                  >
                    <option value="">Escolha o rolo…</option>
                    {avail
                      .filter((r) => r.combinacao_id === selCombo)
                      .map((r) => (
                        <option key={r.id} value={r.id}>
                          {r.codigo} — {formatMedida(r.peso_atual_kg, r.unidade)}
                        </option>
                      ))}
                  </select>
                  <button
                    onClick={adicionarSelecionado}
                    disabled={busy || !selRolo}
                    data-testid="rom-add-sel-btn"
                    className="flex h-10 items-center gap-1 rounded-md bg-slate-900 px-3 text-sm font-semibold text-white hover:bg-slate-800 disabled:opacity-40"
                  >
                    {busy ? <Loader2 className="animate-spin" size={16} /> : <Plus size={16} />} Adicionar
                  </button>
                </div>

                <div className="my-2 text-center text-xs text-slate-400">ou bipe / digite o código</div>
                <div className="flex flex-wrap items-center gap-2">
                  <input
                    value={novoCodigo}
                    onChange={(e) => setNovoCodigo(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && adicionarRolo()}
                    placeholder="Ex.: ROLO-0123"
                    data-testid="rom-add-codigo"
                    className="h-10 flex-1 rounded-md border-2 border-slate-300 px-3 text-sm outline-none focus:border-blue-500"
                  />
                  <button
                    onClick={adicionarRolo}
                    disabled={busy}
                    data-testid="rom-add-btn"
                    className="flex h-10 items-center gap-1 rounded-md border-2 border-slate-300 px-3 text-sm font-semibold text-slate-700 hover:bg-slate-100 disabled:opacity-40"
                  >
                    <Plus size={16} /> Adicionar
                  </button>
                </div>
              </div>
            )}

            {detalhe.status === "Pendente" && (canEditar || canCancelar) && (
              <div className="mt-4 flex flex-wrap items-center gap-2">
                {canEditar && (
                <button
                  onClick={() => { const nv = !editando; setEditando(nv); if (nv) loadAvail(); }}
                  data-testid="rom-editar-toggle"
                  className={`flex items-center gap-2 rounded-md border-2 px-4 py-2.5 text-sm font-semibold ${editando ? "border-green-300 bg-green-50 text-green-700" : "border-slate-300 text-slate-700 hover:bg-slate-100"}`}
                >
                  <Pencil size={16} /> {editando ? "Concluir edição" : "Editar rolos"}
                </button>
                )}
                {canCancelar && (
                <button
                  onClick={() => setCancelConfirm(detalhe)}
                  data-testid="rom-cancelar-btn"
                  className="flex items-center gap-2 rounded-md border-2 border-red-300 bg-red-50 px-4 py-2.5 text-sm font-semibold text-red-700 hover:bg-red-100"
                >
                  <Ban size={16} /> Cancelar romaneio
                </button>
                )}
              </div>
            )}

            {canPdf && (
            <div className="mt-3 grid grid-cols-2 gap-2">
              <button
                onClick={() => abrirPdf(detalhe)}
                data-testid="romaneio-detalhe-pdf"
                className="flex items-center justify-center gap-2 rounded-md bg-slate-900 px-4 py-3 text-sm font-semibold text-white hover:bg-slate-800"
              >
                <Printer size={16} /> PDF
              </button>
              <button
                onClick={() => compartilhar(detalhe)}
                data-testid="romaneio-detalhe-share"
                className="flex items-center justify-center gap-2 rounded-md bg-blue-600 px-4 py-3 text-sm font-semibold text-white hover:bg-blue-700"
              >
                <Share2 size={16} /> Compartilhar
              </button>
            </div>
            )}
          </div>
        </div>
      )}

      {cancelConfirm && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4" onClick={() => !busy && setCancelConfirm(null)}>
          <div className="w-full max-w-md rounded-md bg-white p-5" onClick={(e) => e.stopPropagation()} data-testid="rom-cancel-dialog">
            <div className="flex items-center gap-2 text-red-700">
              <AlertTriangle size={22} />
              <h3 className="text-lg font-bold">Cancelar romaneio</h3>
            </div>
            <p className="mt-3 text-sm text-slate-600">
              Cancelar este romaneio (REF {cancelConfirm.ref}) vai <strong>devolver todos os {cancelConfirm.total_rolos} rolos ao estoque</strong> (status Disponível) e marcar o romaneio como <strong>Cancelado</strong>. O romaneio continua registrado no histórico. Tem certeza?
            </p>
            <div className="mt-4 flex justify-end gap-2">
              <button
                onClick={() => setCancelConfirm(null)}
                disabled={busy}
                className="rounded-md border-2 border-slate-200 px-4 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-100"
              >
                Voltar
              </button>
              <button
                onClick={cancelarRomaneio}
                disabled={busy}
                data-testid="rom-cancel-confirm"
                className="flex items-center gap-2 rounded-md bg-red-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-red-700 disabled:opacity-40"
              >
                {busy && <Loader2 className="animate-spin" size={16} />} Sim, cancelar romaneio
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
